import { FC, useEffect, useState } from 'react';
import { FormikProps, useFormikContext } from 'formik';
import { Home2Icon } from '../../../../../assets/icons';
import Input from '../../../../../components/Input';
import SelectComponent from '../../../../../components/SelectComponent';
import { FieldTypes, InputType } from '../../../../../utils/@globalTypes';
import {
  ADDRESS_ERROR,
  ADDRESS_REQ,
  IMAGE_ERROR,
  LOCALITY_ERROR,
  LOCALITY_REQ,
  MAIN_OBJECT_IMAGE_REQ,
  OBJECT_NAME_ERROR,
  OBJECT_NAME_REQ,
  REGION_ERROR,
  REGION_REQ,
  RENTAL_DESCRIPTION_REQ,
  RULES_PDF_FILE_REQ,
} from '../../../../../utils/constants';
import styles from './BasicSettingsForm.module.scss';
import {
  BasicSettingsType,
  MainObject,
} from '../../../../../redux/types/mainObjectTypes';
import { banDoubleSpace, getErrorText } from '../../../../../utils/functions';
import { useDispatch, useSelector } from 'react-redux';
import { MainObjectSelectors } from '../../../../../redux/reducers/mainObjectSlice';
import FormikImageWrapper from '../../../../../components/FormikImageWrapper';
import {
  AuthSelectors,
  setErrorResponeData,
} from '../../../../../redux/reducers/authSlice';
import LoadingPDF from '../../../../../components/LoadingPDF';
import TextEditor from '../../../../../components/TextEditor';

type BasicFormProps = {
  formik: FormikProps<BasicSettingsType>;
  currentObject: MainObject | null;
};

const BasicSettingsForm: FC<BasicFormProps> = ({ formik, currentObject }) => {
  const { setFieldValue, setFieldError } = useFormikContext();
  const dispatch = useDispatch();
  const {
    handleChange,
    values,
    errors,
    setFieldTouched,
    touched,
    validateField,
  } = formik;
  const objectTypes = useSelector(MainObjectSelectors.getMainObjectTypesData);
  const countriesList = useSelector(MainObjectSelectors.getCountriesOptions);
  const errorResponseData = useSelector(AuthSelectors.getErrorResponseData);

  useEffect(() => {
    if (errorResponseData) {
      errorResponseData.photo &&
        setFieldError(
          'newHomeImage',
          getErrorText(
            errorResponseData.photo[0],
            IMAGE_ERROR,
            FieldTypes.MAIN_OBJECT_PHOTO,
          ),
        );

      errorResponseData.booking_photo &&
        setFieldError(
          'newBookingImage',
          getErrorText(
            errorResponseData.booking_photo[0],
            IMAGE_ERROR,
            FieldTypes.MAIN_OBJECT_BOOKING_PHOTO,
          ),
        );

      errorResponseData.name &&
        setFieldError(
          'objectName',
          getErrorText(
            errorResponseData.name[0],
            OBJECT_NAME_ERROR,
            FieldTypes.OBJECT_NAME,
          ),
        );

      errorResponseData.region &&
        setFieldError(
          'region',
          getErrorText(
            errorResponseData.region[0],
            REGION_ERROR,
            FieldTypes.REGION,
          ),
        );

      errorResponseData.locality &&
        setFieldError(
          'locality',
          getErrorText(
            errorResponseData.locality[0],
            LOCALITY_ERROR,
            FieldTypes.LOCALITY,
          ),
        );

      errorResponseData.address &&
        setFieldError(
          'address',
          getErrorText(
            errorResponseData.address[0],
            ADDRESS_ERROR,
            FieldTypes.ADDRESS,
          ),
        );

      dispatch(setErrorResponeData(null));
    }
  }, [errorResponseData]);

  useEffect(() => {
    if (touched.address) {
      validateField('address');
    }
    if (touched.locality) {
      validateField('locality');
    }
    if (touched.region) {
      validateField('region');
    }
    if (touched.country) {
      validateField('country');
    }
    if (touched.objectName) {
      validateField('objectName');
    }
    if (touched.objectType) {
      validateField('objectType');
    }
    if (touched.rulesPdf) {
      validateField('rulesPdf');
    }
    if (touched.textRules) {
      validateField('textRules');
    }
  }, [values, touched]);
  
  return (
    <div className={styles.basicSettingsWrapper}>
      <div className={styles.photoWrapper}>
        <div className={styles.bookingPhotoWrapper}>
          <h3 className={styles.contentTitle}>
            Фото <span>(для букинга)</span>
          </h3>
          <FormikImageWrapper
            photoFormikName="bookingImage"
            newPhotoFormikName="newBookingImage"
            noImageIcon={<Home2Icon />}
            maxFileSize={2000000}
            acceptType={['jpg', 'jpeg', 'png']}
            resolutionType={'less'}
            resolutionHeight={2000}
            resolutionWidth={2000}
            // maxNumber={1}
            allowNonImageType={true}
            alt={currentObject?.name}
            requirementsText={MAIN_OBJECT_IMAGE_REQ}
            classNameImgWrapper={styles.bookingPhoto}
          />
        </div>
        <div className={styles.listPhotoWrapper}>
          <h3 className={styles.contentTitle}>Фото</h3>
          <FormikImageWrapper
            photoFormikName="homeImage"
            newPhotoFormikName="newHomeImage"
            noImageIcon={<Home2Icon />}
            maxFileSize={2000000}
            acceptType={['jpg', 'jpeg', 'png']}
            resolutionType={'less'}
            resolutionHeight={2000}
            resolutionWidth={2000}
            // maxNumber={1}
            alt={currentObject?.name}
            requirementsText={MAIN_OBJECT_IMAGE_REQ}
            classNameImgWrapper={styles.photo}
          />
        </div>
      </div>

      <div className={styles.fieldsWrapper}>
        <h3 className={styles.contentTitle}>Объект</h3>
        <div className={styles.fields}>
          <div className={styles.fieldsItem}>
            <Input
              name="objectName"
              id="objectName"
              value={values.objectName}
              title="Название объекта*"
              placeholder="Введите название объекта"
              type={InputType.TEXT}
              onChange={handleChange('objectName')}
              errText={errors.objectName}
              onBlur={() => setFieldTouched('objectName')}
              requirementsText={OBJECT_NAME_REQ}
              customBanSymbols={banDoubleSpace}
            />
            <SelectComponent
              name="objectType"
              id="objectType"
              title="Тип объекта размещения*"
              optionsList={objectTypes.optionsList}
              currentValue={values.objectType}
              setSelecValue={handleChange('objectType')}
              errText={errors.objectType}
              isLoading={objectTypes.optionsList.length === 0}
              onBlur={() => setFieldTouched('objectType')}
            />
          </div>
          <div className={styles.fieldsItem}>
            <SelectComponent
              name="country"
              id="country"
              title="Страна*"
              optionsList={countriesList}
              currentValue={values.country}
              setSelecValue={handleChange('country')}
              errText={errors.country}
              isLoading={!countriesList}
              onBlur={() => setFieldTouched('country')}
              isSearchable
            />
            <Input
              name="region"
              id="region"
              value={values.region}
              title="Административное деление*"
              placeholder="Введите административное деление"
              type={InputType.TEXT}
              onChange={handleChange('region')}
              errText={errors.region}
              onBlur={() => setFieldTouched('region')}
              requirementsText={REGION_REQ}
              customBanSymbols={banDoubleSpace}
            />
            <Input
              name="locality"
              id="locality"
              value={values.locality}
              title="Населенный пункт*"
              placeholder="Введите населенный пункт"
              type={InputType.TEXT}
              onChange={handleChange('locality')}
              errText={errors.locality}
              onBlur={() => setFieldTouched('locality')}
              requirementsText={LOCALITY_REQ}
              customBanSymbols={banDoubleSpace}
            />
            <Input
              name="address"
              id="address"
              value={values.address}
              title="Адрес*"
              placeholder="Введите адрес"
              type={InputType.TEXT}
              onChange={handleChange('address')}
              errText={errors.address}
              onBlur={() => setFieldTouched('address')}
              requirementsText={ADDRESS_REQ}
              customBanSymbols={banDoubleSpace}
            />
            {/* <LoadingPDF
              title="Правила проживания для гостей"
              errText={errors.rulesPdf}
              formikName="rulesPdf"
              requirementsText={RULES_PDF_FILE_REQ}
            /> */}
          </div>
        </div>
        <TextEditor
          title="Правила усадьбы"
          onChange={handleChange("textRules")}
          value={values.textRules}
          onBlur={() => setFieldTouched('textRules')}
          requirementsText={RENTAL_DESCRIPTION_REQ}
          errText={errors.textRules}
          setError={()=>{}}
          limit={10000}
        />
      </div>
    </div>
  );
};
export default BasicSettingsForm;
